import React, { useEffect, useState } from "react";
import { useApp } from "@/contexts/AppContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Printer, ArrowLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";

export default function QrPrintSheet() {
    const { t, lang } = useApp();
    const navigate = useNavigate();
    const [from, setFrom] = useState(1);
    const [to, setTo] = useState(30);
    const [origin, setOrigin] = useState("");
    const [baseUrl, setBaseUrl] = useState("");

    useEffect(() => {
        const o = window.location.origin;
        setOrigin(o);
        // Prefer production domain if Owner is currently on preview env
        const saved = localStorage.getItem("cafe24_qr_base_url");
        setBaseUrl(saved || (o.includes("preview.emergentagent") ? "https://cafe24.space" : o));
    }, []);

    const effectiveBase = (baseUrl || origin).replace(/\/+$/, "");

    const tables = [];
    const f = Math.max(1, Number(from) || 1);
    const tt = Math.min(70, Math.max(f, Number(to) || f));
    for (let i = f; i <= tt; i++) tables.push(i);

    const qrUrl = (n) =>
        `https://api.qrserver.com/v1/create-qr-code/?size=180x180&margin=4&color=1A0F08&bgcolor=FDFAF5&format=png&data=${encodeURIComponent(`${effectiveBase}/menu?table=${n}`)}`;

    const handlePrint = () => window.print();
    const saveBaseUrl = (v) => {
        setBaseUrl(v);
        localStorage.setItem("cafe24_qr_base_url", v);
    };

    const isAr = lang === "ar";

    return (
        <>
            {/* Print-only stylesheet */}
            <style>{`
              @media print {
                body { background: #fff !important; color: #000 !important; }
                .no-print { display: none !important; }
                .qr-sheet { background: #fff !important; padding: 0 !important; }
                .qr-card {
                  background: #fff !important;
                  border: 1px solid #1A0F08 !important;
                  break-inside: avoid;
                  page-break-inside: avoid;
                }
                .qr-card .label, .qr-card .wordmark { color: #1A0F08 !important; }
                @page { size: A4 portrait; margin: 10mm; }
              }
            `}</style>

            <div className="space-y-4 qr-sheet" data-testid="qr-print-page">
                <div className="flex items-center justify-between flex-wrap gap-3 no-print">
                    <div className="flex items-center gap-3">
                        <Button variant="ghost" size="icon" onClick={() => navigate("/dashboard/tables")} data-testid="back-tables">
                            <ArrowLeft className="h-5 w-5 flip-rtl" />
                        </Button>
                        <h1 className={`${isAr ? "font-ar" : "font-display-en italic"} text-3xl`}>
                            {isAr ? "طباعة QR للطاولات" : "Print Table QR Stickers"}
                        </h1>
                    </div>
                    <Button onClick={handlePrint} size="lg" data-testid="print-qr-sheet-btn">
                        <Printer className="h-5 w-5 me-2" />
                        {isAr ? "طباعة" : "Print"}
                    </Button>
                </div>

                <Card className="grain bg-card/70 no-print">
                    <CardContent className="p-5">
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 items-end">
                            <div>
                                <Label className="text-xs">{isAr ? "من طاولة" : "From table"}</Label>
                                <Input type="number" min="1" max="70" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="qr-from-input" />
                            </div>
                            <div>
                                <Label className="text-xs">{isAr ? "إلى طاولة" : "To table"}</Label>
                                <Input type="number" min="1" max="70" value={to} onChange={(e) => setTo(e.target.value)} data-testid="qr-to-input" />
                            </div>
                            <div className="col-span-2 text-sm text-muted-foreground">
                                {isAr
                                    ? `سيتم طباعة ${tables.length} رمز QR على ورقة A4. تنتشر تلقائياً 5 أعمدة × 6 صفوف.`
                                    : `${tables.length} QR codes will print on A4. Auto-arranges in 5 columns × 6 rows.`}
                            </div>
                            <div className="col-span-2 sm:col-span-4">
                                <Label className="text-xs">
                                    {isAr ? "رابط الموقع (للـ QR)" : "Base URL (for QR codes)"}
                                </Label>
                                <Input
                                    type="url"
                                    value={baseUrl}
                                    onChange={(e) => saveBaseUrl(e.target.value)}
                                    placeholder="https://cafe24.space"
                                    dir="ltr"
                                    data-testid="qr-base-url-input"
                                />
                                <div className="text-[11px] text-muted-foreground mt-1 break-all" dir="ltr">
                                    {isAr ? "مثال للرابط: " : "Example: "}
                                    {effectiveBase}/menu?table=1
                                </div>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {/* QR Grid — 5 columns × 6 rows */}
                <div
                    className="grid gap-2 mx-auto bg-white p-4 rounded-xl"
                    style={{
                        gridTemplateColumns: "repeat(5, 1fr)",
                        maxWidth: "210mm",
                    }}
                    data-testid="qr-grid"
                >
                    {tables.map((n) => (
                        <div
                            key={n}
                            className="qr-card flex flex-col items-center justify-center border border-[#1A0F08] rounded-lg bg-[#FDFAF5] p-2"
                            style={{ aspectRatio: "1 / 1.15" }}
                            data-testid={`qr-cell-${n}`}
                        >
                            <div className="wordmark font-display-en italic text-[10pt] font-semibold text-[#1A0F08] leading-none mb-1">Café 24</div>
                            <img
                                src={qrUrl(n)}
                                alt={`Table ${n}`}
                                className="w-full max-w-[26mm] flex-1 object-contain"
                                loading="lazy"
                            />
                            <div className="label mt-1 text-[#1A0F08]">
                                <div className="font-ar text-[9pt] leading-none">طاولة</div>
                                <div className="font-display-en text-[14pt] font-bold tabular-nums leading-none mt-0.5">{n}</div>
                            </div>
                        </div>
                    ))}
                </div>

                <div className="text-center text-xs text-muted-foreground no-print pt-2">
                    {isAr
                        ? "نصيحة: طبّق Ctrl+P (أو Cmd+P) → اختر A4 → بدون هوامش → ابدأ الطباعة"
                        : "Tip: Ctrl+P (or Cmd+P) → A4 → no margins → Print"}
                </div>
            </div>
        </>
    );
}
